/* eslint-disable indent */
/* eslint-disable react/jsx-indent-props */
/* eslint-disable react/jsx-indent */
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark, faPaperPlane, faCar, faMagnifyingGlass, faScrewdriverWrench,
} from '@fortawesome/free-solid-svg-icons';
import { GreeniaAIAgentModalStyles } from './styles/GreeniaAIAgent.styles';
import { generalAlertError } from '../../common/utils/alerts';
import { chatAxe } from '../../services/axios-client/chatAxeClient';
import AiMessage from './parts/AiMessage';
import UserMessage from './parts/UserMessage';
import GreeniaMark from './parts/GreeniaMark';
import { updateMessages } from './helper/messagesFormatter';

const SUGGESTIONS = [
    { icon: faCar, text: 'Genera un preventivo Auto' },
    { icon: faMagnifyingGlass, text: 'Cerca una polizza dal numero' },
    { icon: faScrewdriverWrench, text: 'Apri un sinistro su una polizza' },
];

function GreeniaAIAgentModal({ setShowGreeniaAI }) {
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const toBottomRef = useRef(null);
    const inputRef = useRef(null);
    const [thread, setThread] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);

    // generateAutoQuote returns a jobId immediately and computes in the background (~85s of scrapers
    // plus the slow Facile leg). Poll the job: append the fast quotes (Adriatic + Prima + Verti/
    // Nobis/Genial Più) as a quote card the moment they're ready, then the Facile follow-up as text.
    const pollQuoteJob = async (jobId) => {
        setQuoteLoading(true);
        // fast lands ~85s in; Facile then polls up to ~200s, so allow ~5 min before giving up.
        const deadline = Date.now() + 300000;
        let fastDone = false;
        let facileDone = false;
        try {
            while (Date.now() < deadline) {
                let data = null;
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const res = await chatAxe.post('/broker-ai/quote/poll', { jobId });
                    if (res.status === 200) data = res.data;
                    else if (res.status === 404) data = { status: 'error', error: 'job_not_found' };
                } catch (e) {
                    // transient error — keep polling until the deadline
                }

                if (data) {
                    if (data.fast?.ready && !fastDone) {
                        fastDone = true;
                        setMessages((prevMessages) => [...prevMessages, {
                            message: data.fast.message,
                            type: 'ai',
                            newQuote: { quote: data.fast.quote, fastQuoteId: data.fast.fastQuoteId },
                        }]);
                    }
                    if (data.facile?.ready && !facileDone) {
                        facileDone = true;
                        if (data.facile.message) {
                            setMessages((prevMessages) => [...prevMessages,
                                { message: data.facile.message, type: 'ai' }]);
                        }
                    }
                    if (data.status === 'error' && !fastDone) {
                        setMessages((prevMessages) => [...prevMessages, {
                            message: 'Si è verificato un errore durante il calcolo del preventivo. Riprova più tardi.',
                            type: 'ai',
                        }]);
                        break;
                    }
                    if (data.status === 'complete' || (fastDone && facileDone)) break;
                }
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => { setTimeout(resolve, 3000); });
            }
        } finally {
            setQuoteLoading(false);
        }
    };

    const sendMessage = async (overrideText) => {
        const content = (typeof overrideText === 'string' ? overrideText : message).trim();
        if (content === '') {
            generalAlertError('Per inviare un messaggio devi scrivere qualcosa');
            return;
        }

        const newHumanMessage = { message: content, type: 'human' };
        const newThread = thread === null ? Date.now().toString() : thread;
        if (thread === null) {
            setThread(newThread);
        }

        setMessages((prevMessages) => [...prevMessages, newHumanMessage]);
        setMessage('');
        setLoading(true);

        try {
            const response = await chatAxe.post('/broker-ai/messages/send', {
                message: newHumanMessage.message,
                thread: newThread,
            });

            if (response.status !== 200) {
                generalAlertError('Si è verificato un errore durante l\'invio del messaggio');
                return;
            }

            const newAiMessage = {
                message: response.data.result,
                type: 'ai',
                quote: null,
                accident: null,
                suspension: null,
                replacement: null,
                cancellation: null,
                modification: null,
                newQuote: null,
                errorNewQuote: null,
                professionQuote: null,
                commercialQuote: null,
                travelQuote: null,
                professioniSanitaQuote: null,
                mediciDipendentiQuote: null,
                medicoProtettoQuote: null,
                medicoLiberoProfessionistaQuote: null,
                houseQuote: null,
                condominioQuote: null,
                rcFamigliaQuote: null,
            };

            updateMessages(messages, response, newAiMessage);
            setMessages((prevMessages) => [...prevMessages, newAiMessage]);

            const jobId = response.data?.quoteJob?.jobId;
            if (jobId) {
                pollQuoteJob(String(jobId));
            }
        } catch (error) {
            generalAlertError('Si è verificato un errore durante l\'invio del messaggio');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!loading) sendMessage();
        }
    };

    const handleSuggestion = (text) => {
        setMessage(text);
        if (inputRef.current) inputRef.current.focus();
    };

    useEffect(() => {
        if (toBottomRef.current) {
            toBottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, loading, quoteLoading]);

    useEffect(() => {
        const onEsc = (e) => {
            if (e.key === 'Escape') setShowGreeniaAI(false);
        };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [setShowGreeniaAI]);

    return (
        <GreeniaAIAgentModalStyles
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) setShowGreeniaAI(false);
            }}
        >
            <div className="chat-card" role="dialog" aria-modal="true" aria-label="Greenia Agente IA">
                <div className="chat-header">
                    <div className="avatar">
                        <GreeniaMark />
                    </div>
                    <div className="head-info">
                        <p className="head-title">Greenia Agente IA</p>
                        <span className="head-status">
                            <span className="dot" />
                            Assistente virtuale · Online
                        </span>
                    </div>
                    <button type="button" className="close-btn" aria-label="Chiudi" onClick={() => setShowGreeniaAI(false)}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="chat-body">
                    {messages.length === 0 && (
                        <div className="empty-state">
                            <div className="big-avatar">
                                <GreeniaMark />
                            </div>
                            <h3>Ciao, sono Greenia</h3>
                            <p>
                                Posso aiutarti a generare preventivi, consultare polizze esistenti e gestire
                                sinistri, sospensioni, sostituzioni, cancellazioni e modifiche dei dati.
                                Da dove vuoi iniziare?
                            </p>
                            <div className="suggested">
                                {SUGGESTIONS.map((s) => (
                                    <button key={s.text} type="button" className="chip" onClick={() => handleSuggestion(s.text)}>
                                        <FontAwesomeIcon icon={s.icon} className="chip-ico" />
                                        {s.text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {messages.map((msg, index) => (
                        <React.Fragment key={index}>
                            {msg.type === 'ai' && (
                                <AiMessage
                                    setShowGreeniaAI={setShowGreeniaAI}
                                    message={msg.message}
                                    quote={msg.quote}
                                    accident={msg.accident}
                                    suspension={msg.suspension}
                                    replacement={msg.replacement}
                                    cancellation={msg.cancellation}
                                    modification={msg.modification}
                                    errorNewQuote={msg.errorNewQuote}
                                    newQuote={msg?.newQuote?.quote}
                                    fastQuoteId={msg?.newQuote?.fastQuoteId}
                                    professionQuote={msg.professionQuote}
                                    commercialQuote={msg.commercialQuote}
                                    travelQuote={msg.travelQuote}
                                    professioniSanitaQuote={msg.professioniSanitaQuote}
                                    mediciDipendentiQuote={msg.mediciDipendentiQuote}
                                    medicoProtettoQuote={msg.medicoProtettoQuote}
                                    medicoLiberoProfessionistaQuote={msg.medicoLiberoProfessionistaQuote}
                                    houseQuote={msg.houseQuote}
                                    condominioQuote={msg.condominioQuote}
                                    rcFamigliaQuote={msg.rcFamigliaQuote}
                                />
                            )}
                            {msg.type === 'human' && <UserMessage message={msg.message} />}
                        </React.Fragment>
                    ))}

                    {(loading || quoteLoading) && (
                        <div className="msg-row ai">
                            <div className="msg-avatar">
                                <GreeniaMark />
                            </div>
                            <div className="bubble">
                                <div className="typing">
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={toBottomRef} />
                </div>

                <div className="chat-input">
                    <textarea
                        ref={inputRef}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        value={message}
                        rows="1"
                        placeholder="Scrivi un messaggio..."
                        disabled={loading}
                    />
                    <button
                        type="button"
                        className="send-btn"
                        onClick={() => sendMessage()}
                        disabled={loading || message.trim() === ''}
                        aria-label="Invia"
                    >
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                </div>
            </div>
        </GreeniaAIAgentModalStyles>
    );
}

export default GreeniaAIAgentModal;
